import { useCallback, useEffect, useRef } from "react";
import { useQuery } from "@design-lab/shared/graphql";
import { GetDesignProjectDocument } from "graphql-api/operations";
import type { models } from "jiffy-api";
import { mapMadeDesignProjectToGetDesignProjectResponse } from "@design-lab/entities/composition/lib/design-project";
import { useMadeEdit } from "../../api/useMadeEdit";

export type UseProjectLoaderParams = {
  uuid?: string;
  isEditMode: boolean;
  assemblyGroupId?: string;
  isSample: boolean;
  shouldLoadProject: boolean;
  createNew?: boolean;
};

export type UseProjectLoaderReturn = {
  projectData: models.designLab.getLatestDesignProject.Response | undefined;
  savedProject: any; // Raw GraphQL response for backward compatibility
  isLoading: boolean;
  error: any;
  projectUuid: string | undefined;
  refetch: () => void;
};

export function useProjectLoader(params: UseProjectLoaderParams): UseProjectLoaderReturn {
  const {
    uuid,
    isEditMode,
    assemblyGroupId,
    isSample,
    shouldLoadProject,
    createNew = false
  } = params;

  const hasTriggeredRef = useRef(false);

  // GraphQL query for regular design project
  const {
    data: savedProject,
    loading: isLoadingProject,
    error: projectError,
    refetch: refetchProject
  } = useQuery(GetDesignProjectDocument, {
    skip: (!shouldLoadProject && !uuid) || isEditMode,
    variables: {
      sample: isSample,
      uuid,
      new: !uuid && createNew
    }
  });

  // API call for edit mode
  const {
    data: editModeProject,
    isLoading: isLoadingEditMode,
    error: editModeError,
    refetch: refetchEditMode
  } = useMadeEdit(isEditMode ? assemblyGroupId : undefined);

  // Transform GraphQL response to standard format
  const projectData: models.designLab.getLatestDesignProject.Response | undefined = isEditMode
    ? editModeProject
    : mapMadeDesignProjectToGetDesignProjectResponse(savedProject?.madeDesignProject);

  // Determine which loading/error states to use
  const isLoading = isEditMode ? isLoadingEditMode : isLoadingProject;
  const error = isEditMode ? editModeError : projectError;
  const projectUuid = savedProject?.madeDesignProject?.uuid;

  // Handle project creation trigger
  useEffect(() => {
    if (createNew && !uuid && !hasTriggeredRef.current && shouldLoadProject) {
      hasTriggeredRef.current = true;
      refetchProject();
    }
  }, [createNew, uuid, shouldLoadProject, refetchProject]);

  // Reset trigger when UUID changes
  useEffect(() => {
    if (uuid) {
      hasTriggeredRef.current = false;
    }
  }, [uuid]);

  const refetch = useCallback(() => {
    if (isEditMode) {
      refetchEditMode();
    } else {
      refetchProject();
    }
  }, [isEditMode, refetchEditMode, refetchProject]);

  return {
    projectData,
    savedProject: isEditMode ? undefined : savedProject, // Return raw GraphQL response for backward compatibility
    isLoading,
    error,
    projectUuid,
    refetch
  };
}
